import { useState, useEffect, useCallback } from "react";
import {
  CalendarDays,
  Clock,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  CopyPlus,
  CopyCheck,
  Coffee,
} from "lucide-react";
import { Badge } from "../ui";
import { getSchedulePlans } from "../../api";

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MOIS_COURT = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Jun",
  "Jul",
  "Aoû",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

function calcDuree(debut, fin, pauseMin = 0) {
  if (!debut || !fin) return "";
  const [hd, md] = debut.split(":").map(Number);
  const [hf, mf] = fin.split(":").map(Number);
  const total = hf * 60 + mf - (hd * 60 + md) - parseInt(pauseMin || 0);
  if (total <= 0) return "—";
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

function fmtDateFr(d) {
  if (!d) return "";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
}

function fmtMin(m) {
  if (!m) return "0h";
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h${String(rm).padStart(2, "0")}` : `${h}h`;
}

function pctOfYear(date, annee) {
  const d = new Date(date);
  const debut = new Date(annee, 0, 1);
  const fin = new Date(annee, 11, 31);
  return Math.min(100, Math.max(0, ((d - debut) / (fin - debut)) * 100));
}

// ─── Grille lecture seule (+ duplication) ────────────────────────────────────

function GrilleReadOnly({
  plan,
  employeeId,
  onDupliquerSemaine,
  onDupliquerToutCycle,
  saving,
}) {
  const semaines = Array.from({ length: plan.nbSemaines }, (_, i) => i + 1);

  function getSlot(weekNumber, dayOfWeek) {
    return (
      plan.slots?.find(
        (s) =>
          s.employeeId === employeeId &&
          s.weekNumber === weekNumber &&
          s.dayOfWeek === dayOfWeek,
      ) ?? null
    );
  }

  function calcTotalSemaine(sem) {
    let total = 0;
    JOURS.forEach((_, ji) => {
      const s = getSlot(sem, ji + 1);
      if (s?.startTime && s?.endTime) {
        const [h1, m1] = s.startTime.split(":").map(Number);
        const [h2, m2] = s.endTime.split(":").map(Number);
        total += h2 * 60 + m2 - (h1 * 60 + m1) - (s.pauseMinutes || 0);
      }
    });
    return total > 0 ? total : 0;
  }

  let totalMin = 0;
  semaines.forEach((sem) => {
    totalMin += calcTotalSemaine(sem);
  });
  const moyMin =
    plan.nbSemaines > 0 ? Math.round(totalMin / plan.nbSemaines) : 0;
  const s1HasSlots = JOURS.some((_, ji) => getSlot(1, ji + 1));
  const canDuplicate = !!onDupliquerSemaine && plan.nbSemaines > 1;

  return (
    <div>
      {/* Stats rapides */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {totalMin > 0 && (
          <>
            <div className="flex items-center gap-1.5 text-xs bg-primary/8 text-primary rounded-lg px-3 py-1.5">
              <Clock size={11} />
              <span className="font-medium">Cycle : {fmtMin(totalMin)}</span>
            </div>
            {plan.nbSemaines > 1 && (
              <div className="flex items-center gap-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg px-3 py-1.5">
                <Clock size={11} />
                <span>Moy. {fmtMin(moyMin)} / semaine</span>
              </div>
            )}
          </>
        )}
        {saving && (
          <span className="text-[11px] text-primary animate-pulse ml-auto">
            Copie en cours…
          </span>
        )}
      </div>

      {/* Grille */}
      <div className="overflow-x-auto">
        <table
          className="border-collapse text-sm"
          style={{
            minWidth: `${60 + JOURS.length * 95 + (canDuplicate ? 80 : 0)}px`,
          }}
        >
          <thead>
            <tr>
              <th className="w-14 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide pb-2 pr-2" />
              {JOURS.map((j, ji) => (
                <th
                  key={ji}
                  className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-2 px-1 text-center min-w-[85px]"
                >
                  {j}
                </th>
              ))}
              <th className="w-14 text-[10px] font-semibold text-gray-400 uppercase tracking-wide pb-2 text-center">
                Total
              </th>
              {canDuplicate && <th className="w-20 pb-2" />}
            </tr>
          </thead>
          <tbody>
            {semaines.map((sem) => {
              const totalSem = calcTotalSemaine(sem);
              return (
                <tr
                  key={sem}
                  className={sem > 1 ? "border-t border-gray-100" : ""}
                >
                  <td className="py-1.5 pr-2 align-middle">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      S{sem}
                    </span>
                  </td>
                  {JOURS.map((_, ji) => {
                    const slot = getSlot(sem, ji + 1);
                    const duree = slot
                      ? calcDuree(
                          slot.startTime,
                          slot.endTime,
                          slot.pauseMinutes,
                        )
                      : "";
                    return (
                      <td key={ji} className="px-1 py-1">
                        <div
                          className={`min-h-[46px] rounded-lg border text-xs flex flex-col items-center justify-center gap-0.5 py-1 px-1 ${
                            slot
                              ? "bg-primary/8 border-primary/25 text-primary-dark"
                              : "bg-gray-50 border-dashed border-gray-200 text-gray-300"
                          }`}
                        >
                          {slot ? (
                            <>
                              <span className="font-semibold text-[11px] leading-tight">
                                {slot.startTime}–{slot.endTime}
                              </span>
                              {slot.pauseMinutes > 0 && (
                                <span className="text-[10px] opacity-70 flex items-center gap-0.5">
                                  <Coffee size={9} /> {slot.pauseMinutes} min
                                </span>
                              )}
                              <span className="text-[10px] text-primary/70">
                                {duree}
                              </span>
                            </>
                          ) : (
                            <span className="text-base leading-none">—</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {/* Total semaine */}
                  <td className="px-1 py-1 align-middle text-center">
                    {totalSem > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/8 rounded-md px-2 py-1 whitespace-nowrap">
                        <Clock size={10} />
                        {fmtMin(totalSem)}
                      </span>
                    ) : (
                      <span className="text-[11px] text-gray-300">—</span>
                    )}
                  </td>
                  {/* Actions duplication */}
                  {canDuplicate && (
                    <td className="px-1 py-1 align-middle">
                      <div className="flex flex-col gap-1">
                        {sem < plan.nbSemaines && (
                          <button
                            disabled={saving}
                            onClick={() => onDupliquerSemaine(sem)}
                            title={`Copier S${sem} → S${sem + 1}`}
                            className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-primary disabled:opacity-40 transition-colors px-2 py-1 rounded hover:bg-primary/5"
                          >
                            <CopyPlus size={11} />
                            <span>→S{sem + 1}</span>
                          </button>
                        )}
                        {sem === 1 && s1HasSlots && onDupliquerToutCycle && (
                          <button
                            disabled={saving}
                            onClick={onDupliquerToutCycle}
                            title="Appliquer S1 à toutes les semaines"
                            className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-accent-dark disabled:opacity-40 transition-colors px-2 py-1 rounded hover:bg-accent/5"
                          >
                            <CopyCheck size={11} />
                            <span>→ tout</span>
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Timeline mini ────────────────────────────────────────────────────────────

function TimelineMini({ plans, activePlanId }) {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const today = new Date();
  const overrides = plans.filter(
    (p) => !p.isReference && p.dateDebut && p.dateFin,
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
          <CalendarDays size={13} className="text-primary" /> Couverture {annee}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setAnnee((a) => a - 1)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <ChevronLeft size={12} />
          </button>
          <button
            onClick={() => setAnnee((a) => a + 1)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-12 mb-1">
        {MOIS_COURT.map((m, i) => (
          <span
            key={i}
            className="text-[9px] text-gray-400 uppercase text-center"
          >
            {m}
          </span>
        ))}
      </div>
      <div className="h-px bg-gray-200 mb-2" />
      <div className="relative h-10">
        <div className="absolute inset-x-0 top-1.5 bottom-1.5 rounded bg-primary/15 border border-primary/20 flex items-center px-2">
          <span className="text-[10px] font-medium text-primary truncate flex items-center gap-1">
            <RotateCcw size={9} />{" "}
            {plans.find((p) => p.isReference)?.nom || "Référence"}
          </span>
        </div>
        {overrides.map((p) => {
          const left = pctOfYear(p.dateDebut, annee);
          const width = pctOfYear(p.dateFin, annee) - left;
          if (width <= 0) return null;
          return (
            <div
              key={p.id}
              title={`${p.nom} : ${fmtDateFr(p.dateDebut)} → ${fmtDateFr(p.dateFin)}`}
              className={`absolute top-0.5 bottom-0.5 rounded flex items-center px-1.5 border overflow-hidden ${p.id === activePlanId ? "bg-accent/40 border-accent/60" : "bg-accent/20 border-accent/40"}`}
              style={{ left: `${left}%`, width: `${width}%` }}
            >
              <span className="text-[9px] font-semibold text-accent-dark truncate">
                {p.nom}
              </span>
            </div>
          );
        })}
        {today.getFullYear() === annee && (
          <div
            className="absolute top-0 bottom-0 w-px bg-red-400"
            style={{ left: `${pctOfYear(today, annee)}%` }}
          >
            <span className="absolute -top-0.5 -translate-x-1/2 block w-1.5 h-1.5 rounded-full bg-red-400" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded bg-primary/30 inline-block" />
          <span className="text-[9px] text-gray-400">Référence</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
          <span className="text-[9px] text-gray-400">Aujourd'hui</span>
        </div>
      </div>
    </div>
  );
}

// ─── PlanningTab — vue lecture seule employé ──────────────────────────────────

export default function PlanningTab({ employee, structureId }) {
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const structId = structureId || employee?.structureId;

  const load = useCallback(async () => {
    if (!structId) return;
    setLoading(true);
    try {
      const res = await getSchedulePlans(structId);
      const loaded = res.data.plans || [];
      const aId = res.data.activePlanId || null;
      setPlans(loaded);
      setActivePlanId(aId);
      setSelectedPlanId(aId ?? loaded[0]?.id ?? null);
    } catch {
      // erreur silencieuse — la vue reste vide
    } finally {
      setLoading(false);
    }
  }, [structId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
        Chargement du planning…
      </div>
    );
  }

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;
  const empSlots = selectedPlan
    ? (selectedPlan.slots || []).filter((s) => s.employeeId === employee?.id)
    : [];
  const hasSlotsForEmp = empSlots.length > 0;
  const isActivePlan = selectedPlanId === activePlanId;

  return (
    <div className="space-y-4 p-4">
      {/* Sélecteur de plan — si plusieurs plans */}
      {plans.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
            <CalendarDays size={13} className="text-primary shrink-0" />
            <span className="text-xs font-semibold text-gray-600">
              Plans disponibles
            </span>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {plans.map((p) => {
              const pSlots = (p.slots || []).filter(
                (s) => s.employeeId === employee?.id,
              );
              const isActive = p.id === activePlanId;
              const isSelected = p.id === selectedPlanId;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlanId(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    isSelected
                      ? "bg-primary text-white border-primary shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <span>{p.nom}</span>
                  {isActive && (
                    <Badge
                      color={
                        isSelected
                          ? "bg-white/20 text-white"
                          : "bg-green-100 text-green-700"
                      }
                    >
                      Actif
                    </Badge>
                  )}
                  {pSlots.length > 0 && !isSelected && (
                    <span className="text-primary/60">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Plan sélectionné */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-3 border-b border-gray-100"
          style={{
            background:
              "linear-gradient(to right, rgb(var(--color-primary) / 0.05), transparent)",
          }}
        >
          <CalendarDays size={14} className="text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-gray-800">
                {selectedPlan?.nom || "Aucun plan sélectionné"}
              </span>
              {selectedPlan?.isReference && (
                <Badge color="bg-primary/10 text-primary">Référence</Badge>
              )}
              {isActivePlan && selectedPlan && (
                <Badge color="bg-green-100 text-green-700">
                  Actif aujourd'hui
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {selectedPlan?.isReference
                ? `Permanent · ${selectedPlan.nbSemaines} semaine${selectedPlan.nbSemaines > 1 ? "s" : ""} dans le cycle`
                : selectedPlan
                  ? `Du ${fmtDateFr(selectedPlan.dateDebut)} au ${fmtDateFr(selectedPlan.dateFin)} · ${selectedPlan.nbSemaines} semaine${selectedPlan.nbSemaines > 1 ? "s" : ""}`
                  : ""}
            </p>
          </div>
          {/* Compteur créneaux */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 shrink-0">
            <Clock size={11} className="text-primary" />
            {hasSlotsForEmp ? (
              <span>
                {empSlots.length} créneau{empSlots.length > 1 ? "x" : ""}
              </span>
            ) : (
              <span className="italic text-gray-400">Aucun horaire</span>
            )}
          </div>
        </div>

        {/* Grille */}
        <div className="p-4">
          {selectedPlan && hasSlotsForEmp ? (
            <GrilleReadOnly
              plan={selectedPlan}
              employeeId={employee?.id}

            />
          ) : selectedPlan ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              <Clock size={32} className="mx-auto mb-2 opacity-20" />
              <p>
                Aucun horaire saisi pour <strong>{employee?.firstName}</strong>{" "}
                dans ce plan.
              </p>
              <p className="text-xs mt-1">
                Configurez les horaires depuis l'onglet{" "}
                <strong>Planning</strong> (Feuilles de temps).
              </p>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-400 text-sm">
              Aucun plan de planning configuré pour cette structure.
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      {plans.length > 0 && (
        <TimelineMini plans={plans} activePlanId={activePlanId} />
      )}
    </div>
  );
}
